'use client'

import {
  AuthContextProviderProps,
  DeadpoolUser,
  FirebaseUserExtends,
  FirebaseValues,
} from '~types/index'

import { Auth, getAuth, onAuthStateChanged } from 'firebase/auth'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { UseLoggedContext } from '@mercadoni/sinister/dist/cjs/authentication'

import firebaseApp from '@/firebase/config'
import { getClient } from '@/lib/ApolloClient/getClient'
import isSSR from '@/utils/isSSR'

import { getCSRCookieData } from '@/app/api/CSR/getCSRCookieData'
import { relogInDeadpool } from './services'

export const AuthContext = React.createContext<{
  user: FirebaseUserExtends | null
  customer: DeadpoolUser | null
  isInitialized: boolean
  federatedClient: boolean
  firebaseValues?: FirebaseValues | null
  setFirebaseValues?: (val: FirebaseValues) => void
}>({ user: null, customer: null, isInitialized: false, federatedClient: false })

export const useAuthContext = () => React.useContext(AuthContext)

export const AuthContextProvider: FC<AuthContextProviderProps> = ({
  children,
  clientId,
  fbConfig,
  federatedClient,
}) => {
  const [user, setUser] = useState<FirebaseUserExtends | null>(null)
  const [customer, setCustomer] = useState<DeadpoolUser | null>(null)
  const [, setLoading] = useState<boolean>(true)
  const [auth, setauth] = useState<Auth | null>(null)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [firebaseValues, setFirebaseValues] = useState<FirebaseValues | null>()
  const token = getCSRCookieData('_IL-token')

  const { setIsLogged } = UseLoggedContext()

  const clientApollo = getClient({ onError: console.error })

  useEffect(() => {
    if (fbConfig) {
      setauth(getAuth(firebaseApp(fbConfig)))
    }
  }, [fbConfig])

  useEffect(() => {
    if (federatedClient) {
      return
    }
    if (auth) {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          setUser(user)
        } else {
          setUser(null)
          setLoading(false)
        }
      })

      return () => unsubscribe()
    }
  }, [auth, federatedClient])

  const onComplete = useCallback((customerData: DeadpoolUser | null) => {
    setCustomer(customerData)
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    const handleOperation = async () => {
      console.log('🚀 ~ handleOperation')
      if (customer?.id) {
        setIsInitialized(true)
        return
      }
      if (clientId) {
        if (!token) {
          setIsInitialized(true)
          return
        }

        await relogInDeadpool(clientApollo, onComplete)

        return
      }
    }
    handleOperation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, customer?.id, clientId])

  useEffect(() => {
    if (customer?.id) {
      if (!isSSR()) {
        sessionStorage.setItem('customer', JSON.stringify(customer))
      }
      setLoading(false)
      setIsLogged(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer])
  const value = useMemo(
    () => ({
      user,
      customer,
      isInitialized,
      federatedClient,
      firebaseValues,
      setFirebaseValues,
    }),
    [user, customer, isInitialized, federatedClient, firebaseValues]
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
